import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getNewsInner } from "../Service/Api";
import FilterContainer from "../utlis/FilterContainer";
import LazyLoad from "react-lazyload";
import { Link } from "react-router-dom";
import Breadcrumb from "../Component/Breadcrumb";

function NewsInner() {
  const [showData, setShowData] = useState(8);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString()
  );

  // ✅ Fetch news data (no loading/error states)
  const { data: newsData = [] } = useQuery({
    queryKey: ["getNewsInner"],
    queryFn: getNewsInner,
  });

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthOptions = ["All", ...monthNames];

  // ✅ Date Formatter
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date)) return "N/A";
    const day = date.getDate().toString().padStart(2, "0");
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  // ✅ Dummy fallback data
  const news_dummy_data = [
    {
      title: "News Title",
      description: "Stay tuned for latest updates",
      img: "https://webapi.entab.info/api/image/SJSKBUP/public/Images/news.jpg",
    },
  ];

  // ✅ Use fallback if API empty
  const newsList =
    Array.isArray(newsData) && newsData.length > 0 ? newsData : news_dummy_data;

  // ✅ Unique year list
  const yearRanges = [
    ...new Set(
      newsList
        .map((item) => {
          const d = new Date(item.fromDate);
          return !isNaN(d) ? d.getFullYear().toString() : null;
        })
        .filter(Boolean)
    ),
  ].sort((a, b) => b - a);

  // ✅ Filtering
  const filterData = newsList.filter((item) => {
    const date = new Date(item.fromDate);
    const itemYear = date.getFullYear().toString();
    const itemMonth = monthNames[date.getMonth()];

    const matchYear = selectedYear === "All" || itemYear === selectedYear;
    const matchMonth = selectedMonth === "All" || itemMonth === selectedMonth;
    const matchSearch =
      item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchQuery.toLowerCase());

    return matchYear && matchMonth && matchSearch;
  });

  // ✅ Visible news items (pagination)
  const visibleNews = [...filterData]
    .sort((a, b) => new Date(b.fromDate) - new Date(a.fromDate))
    .slice(0, showData);

  const handleLoadMore = () => setShowData((prev) => prev + 8);

  // ✅ Set page title
  useEffect(() => {
    document.title = "St. Joseph’s school, Barhalganj | News";
  }, []);

  return (
    <>
      <Breadcrumb>News</Breadcrumb>

      <div className="innersec">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <h1>News</h1>
            </div>
          </div>

          {/* ===== Filters ===== */}
          <FilterContainer
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            yearRanges={yearRanges}
            monthOptions={monthOptions}
            totalCount={filterData.length}
          />
        </div>

        {/* ===== News Grid ===== */}
        <div className="container">
          <div className="row years year2024 showYear">
            {visibleNews.length > 0 ? (
              visibleNews.map((item, index) => (
                <div
                  className="col-xl-6 col-lg-6 col-md-12 col-sm-12 eventCount"
                  key={index}>
                  <div className="eventsnewsbox">
                    <div className="newsboximg">
                      <LazyLoad
                        offset={10}
                        placeholder={
                          <img
                            src="https://webapi.entab.info/api/image/SJSKBUP/public/Images/news.jpg"
                            alt="Loading..."
                            className="img-fluid"
                          />
                        }>
                        <img
                          src={
                            item?.images?.length
                              ? `https://webapi.entab.info/api/image/${item.images[0]}`
                              : "https://webapi.entab.info/api/image/SJSKBUP/public/Images/news.jpg"
                          }
                          className="img-fluid"
                          alt={item.title || "News"}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src =
                              "https://webapi.entab.info/api/image/SJSKBUP/public/Images/news.jpg";
                          }}
                        />
                      </LazyLoad>
                    </div>

                    <div className="newsboxdesc">
                      <div className="newspgh">
                        <h5>{item.title || "News Title"}</h5>
                        <p>
                          {item.description ||
                            "Stay tuned for more updates and announcements."}
                        </p>
                      </div>

                      <div className="newsdate">
                        <span>
                          <i className="bi bi-calendar"></i>{" "}
                          {formatDate(item.fromDate)}
                        </span>
                        {item?.attachments?.length ? (
                          <Link
                            to={`https://webapi.entab.info/api/image/${item.attachments[0]}`}
                            target="_blank">
                            <i className="bi bi-paperclip"></i>
                          </Link>
                        ) : (
                          <i className="bi bi-paperclip text-muted"></i>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              // ✅ Fallback “No news” block
              <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12 eventCount">
                <div className="eventsnewsbox">
                  <div className="newsboximg">
                    <img
                      src="https://webapi.entab.info/api/image/SJSKBUP/public/Images/news.jpg"
                      className="img-fluid"
                      alt="No News"
                    />
                  </div>
                  <div className="newsboxdesc">
                    <div className="newspgh">
                      <h5>Stay tuned for latest updates</h5>
                      <p>Check back soon for new news and announcements.</p>
                    </div>
                    <div className="newsdate">
                      <span>
                        <i className="bi bi-calendar"></i> DD/MM/YYYY
                      </span>
                      <i className="bi bi-paperclip text-muted"></i>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ===== Load More Button ===== */}
          {filterData.length > showData && (
            <div className="text-center mt-4">
              <button className="mainbtn button" onClick={handleLoadMore}>
                Load More
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default NewsInner;
